import { getCatalogParentBySlug } from '@/lib/catalog-taxonomy';
import type { CatalogMenuItem } from '@/lib/catalog/types';
import { getCatalogPublicHref } from '@/lib/catalog/slug';

function normalizeText(value: string) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function slugifyVietnamese(value: string) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function getSubCategoryHref(parentHref: string, subLabel: string) {
  if (!parentHref.startsWith('/danh-muc/')) return parentHref || '#';
  const parentSlug = parentHref.replace('/danh-muc/', '').trim();
  if (!parentSlug) return parentHref || '#';

  const parent = getCatalogParentBySlug(parentSlug);
  if (!parent || !parent.children.length) return parentHref;

  const matched = parent.children.find(
    (child) => normalizeText(child.label) === normalizeText(subLabel)
  );
  if (!matched) return parentHref;
  return getCatalogPublicHref(matched.slug);
}

export function getMegaSubHref(parentHref: string, subLabel: string) {
  const cleaned = subLabel.replace(/\s*❯\s*$/, '').trim();
  if (parentHref === '/danh-muc/cpu-mainboard-vga') {
    const normalized = normalizeText(cleaned);
    if (normalized === 'cpu') return getCatalogPublicHref('cpu');
    if (normalized === 'mainboard') return getCatalogPublicHref('mainboard');
    if (normalized === 'vga') return getCatalogPublicHref('vga');
  }

  const href = getSubCategoryHref(parentHref, cleaned);
  if (href !== parentHref) return href;

  if (parentHref.startsWith('/danh-muc/')) {
    return getCatalogPublicHref(slugifyVietnamese(cleaned));
  }

  return parentHref || '#';
}

export type MegaSubGroup = {
  title: string;
  items: string[];
};

export function buildGroupedSubs(labelRaw: string, subs: string[]): MegaSubGroup[] | null {
  const label = labelRaw.toLowerCase();
  const pick = (keys: string[]) =>
    subs.filter((s) => keys.some((k) => s.toLowerCase().includes(k)));

  if (label.includes('cpu') && label.includes('mainboard') && label.includes('vga')) {
    return [
      { title: 'CPU', items: ['CPU'] },
      { title: 'Mainboard', items: ['Mainboard'] },
      { title: 'VGA', items: ['VGA'] },
    ];
  }

  let groups: MegaSubGroup[] = [];
  if (label.includes('ram') || label.includes('ssd') || label.includes('hdd')) {
    groups = [
      { title: 'Bộ nhớ RAM', items: pick(['ram']) },
      { title: 'Ổ cứng SSD', items: pick(['ssd']) },
      { title: 'Ổ cứng HDD', items: pick(['hdd', 'hard']) },
    ];
  } else if (label.includes('cpu') || label.includes('mainboard') || label.includes('vga')) {
    groups = [
      { title: 'CPU', items: pick(['cpu']) },
      { title: 'Mainboard', items: pick(['mainboard']) },
      { title: 'VGA', items: pick(['vga', 'card', 'gpu']) },
    ];
  } else if (label.includes('case') || label.includes('tan') || label.includes('nguon')) {
    groups = [
      { title: 'Case', items: pick(['case']) },
      { title: 'Tản nhiệt', items: pick(['tản', 'tan']) },
      { title: 'Nguồn', items: pick(['nguồn', 'nguon', 'psu']) },
    ];
  } else if (label.includes('phim') || label.includes('chuot') || label.includes('tai nghe')) {
    groups = [
      { title: 'Bàn phím', items: pick(['bàn phím', 'ban phim', 'keyboard']) },
      { title: 'Chuột', items: pick(['chuột', 'chuot', 'mouse']) },
      { title: 'Tai nghe', items: pick(['tai nghe', 'headset']) },
    ];
  } else if (label.includes('man hinh')) {
    groups = [];
  } else if (label.includes('ban') || label.includes('ghe')) {
    groups = [
      { title: 'Bàn', items: pick(['bàn', 'ban', 'desk']) },
      { title: 'Ghế', items: pick(['ghế', 'ghe', 'chair']) },
    ];
  } else if (label.includes('mạng') || label.includes('mang')) {
    groups = [
      { title: 'Router', items: pick(['router', 'wifi']) },
      { title: 'Switch', items: pick(['switch']) },
      { title: 'USB WiFi', items: pick(['usb', 'wifi']) },
    ];
  } else if (label.includes('máy in') || label.includes('may in') || label.includes('ups')) {
    groups = [
      { title: 'Máy in', items: pick(['máy in', 'may in', 'printer']) },
      { title: 'UPS', items: pick(['ups']) },
    ];
  } else if (label.includes('camera') || label.includes('webcam')) {
    groups = [
      { title: 'Camera', items: pick(['camera']) },
      { title: 'Webcam', items: pick(['webcam']) },
    ];
  } else if (label.includes('pc')) {
    groups = [
      { title: 'PC Gaming', items: pick(['gaming']) },
      { title: 'PC Văn phòng', items: pick(['văn phòng', 'van phong', 'office']) },
      { title: 'PC AI', items: pick(['ai']) },
      { title: 'Mini PC', items: pick(['mini']) },
    ];
  }

  const filtered = groups.filter((g) => g.items.length);
  if (!filtered.length) return null;

  const used = new Set(filtered.flatMap((g) => g.items));
  const extra = subs.filter((s) => !used.has(s));
  if (extra.length) filtered.push({ title: 'Khác', items: extra });

  return filtered;
}

export function toHeaderMegaMenuItems(items: CatalogMenuItem[]) {
  return items.map((item) => ({
    label: item.label,
    href: item.href,
    icon: item.icon,
    sub: item.sub,
    is_featured: item.is_featured,
  }));
}
